"use client";
// ============================================================
// F6: CyberScore — ИИ-оценка письменных отчётов (инцидент-отчёты,
// форензик-заключения). Ollama qwen3:8b на хосте (офлайн, on-prem)
// + deterministic-фолбэк. Рубрика: 4 критерия × 25 баллов.
// ============================================================
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Sparkles, Loader2 } from "lucide-react";
import { api, post } from "@/lib/api/client";
import { useT } from "@/lib/i18n";
import { RequireAuth } from "@/components/auth";
import { Button } from "@/components/ui";

interface Criterion {
  name: string;
  score: number;
  max: number;
  comment: string;
}
interface GradeResult {
  score: number;
  criteria: Criterion[];
  feedback: string;
  engine: "llm" | "heuristic";
  model?: string;
}
interface HistoryItem {
  id: string;
  scenario_id: string;
  task_id: string;
  score: number;
  engine: string;
  model: string;
  created_at: string;
  json: GradeResult;
}
interface HistoryDto {
  average: number | null;
  total: number;
  items: HistoryItem[];
}

const RUBRIC_EXAMPLE =
  "Пример структуры отчёта:\n1. Инцидент: что произошло, когда, кто атакующий (IP).\n2. Векторы: IDOR /api/orders, SSH brute-force (admin, 27 попыток), RCE через инъекцию в ping.\n3. Масштаб: сколько данных затрагано.\n4. Правовая рамка: УК РК ст. 204, законы «О информации»/«О ПД», УПК РК (цепочка сохранности).\n5. Рекомендации: MFA, access_check, валидация ввода, обновление firmware.";

export function CyberScorePage() {
  const t = useT();
  const [history, setHistory] = useState<HistoryDto | null>(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<GradeResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadHistory = useCallback(async () => {
    try {
      const d = await api<HistoryDto>("/cyberscore");
      setHistory(d);
    } catch {
      /* не авторизован/ошибка — ниже пустое состояние */
    }
  }, []);

  useEffect(() => {
    void loadHistory();
  }, [loadHistory]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (text.trim().length < 20) {
      setError("Минимум 20 символов — иначе нечего оценивать.");
      return;
    }
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const r = await post<GradeResult>("/cyberscore/grade", {
        text: text.trim(),
        llm: true,
      });
      setResult(r);
      void loadHistory();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <RequireAuth>
      <div className="cyberscore-page">
        <div className="cyberscore-hero">
          <div>
            <div className="banner-label">
              <span className="dot" /> AI CYBERSCORE
            </div>
            <h2>
              ИИ-оценка <span>письменных отчётов</span>
            </h2>
            <p>
              Вставьте ваш инцидент-отчёт или форензик-заключение — модель
              оценит по рубрике следователя: точность фактов, структура,
              правовая рамка РК, ясность. Оценки хранятся в вашей истории и
              идут в статистику аттестации.
            </p>
          </div>
          <div className="cyberscore-avg" aria-label="средний балл">
            <div className="num">{history?.average ?? "—"}</div>
            <div className="cap">средний балл</div>
            <div className="sub">оценок: {history?.total ?? 0}</div>
          </div>
        </div>

        <div className="cyberscore-grid">
          <section className="panel cyberscore-form">
            <h2>
              <Sparkles size={18} className="accent" /> Оценить отчёт
            </h2>
            <p className="muted" style={{ marginTop: 0 }}>
              {t("cs.rubric")}
            </p>
            <details className="cs-example">
              <summary>Структура хорошего отчёта</summary>
              <pre>{RUBRIC_EXAMPLE}</pre>
            </details>
            <form onSubmit={submit}>
              <textarea
                className="mono"
                rows={12}
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder={t("cs.placeholder")}
                disabled={busy}
              />
              {error && <p className="cs-error">{error}</p>}
              <div className="inline" style={{ marginTop: 10 }}>
                <Button type="submit" disabled={busy}>
                  {busy ? (
                    <>
                      <Loader2 size={15} className="spin" /> {t("cs.grading")}
                    </>
                  ) : (
                    t("cs.evaluate")
                  )}
                </Button>
                <span className="muted" style={{ fontSize: 13 }}>
                  {t("cs.engine_note")}
                </span>
              </div>
            </form>
          </section>

          <section className="panel cyberscore-result">
            <h2>{t("cs.result_title")}</h2>
            {result ? (
              <>
                <div className="cs-score-row">
                  <div className="cs-score-big">
                    <span className="num">{result.score}</span>
                    <span className="max">/100</span>
                  </div>
                  <div>
                    <Badge engine={result.engine} model={result.model} />
                    <p className="cs-feedback">{result.feedback}</p>
                  </div>
                </div>
                <div className="cs-criteria">
                  {result.criteria.map((c) => (
                    <div className="cs-criterion" key={c.name}>
                      <div className="cs-criterion-head">
                        <span>{c.name}</span>
                        <span className="mono">
                          {c.score}/{c.max}
                        </span>
                      </div>
                      <div className="bar">
                        <div
                          className="bar-fill"
                          style={{ width: `${(c.score / c.max) * 100}%` }}
                        />
                      </div>
                      {c.comment && <p className="muted cs-comment">{c.comment}</p>}
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <p className="muted">{t("cs.result_empty")}</p>
            )}
          </section>
        </div>

        <section className="panel table-wrap">
          <h2>{t("cs.history_title")}</h2>
          <table>
            <thead>
              <tr>
                <th>{t("cs.h_date")}</th>
                <th>{t("cs.h_scenario")}</th>
                <th>{t("cs.h_engine")}</th>
                <th>{t("cs.h_score")}</th>
              </tr>
            </thead>
            <tbody>
              {history?.items.length ? (
                history.items.map((it) => (
                  <tr key={it.id}>
                    <td className="muted">
                      {new Date(it.created_at).toLocaleString("ru-RU")}
                    </td>
                    <td>
                      {it.scenario_id || "—"}
                      {it.task_id ? <span className="muted"> · {it.task_id}</span> : null}
                    </td>
                    <td className="muted">{it.engine}</td>
                    <td>
                      <strong>{it.score}</strong>
                      <span className="muted"> /100</span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="muted">
                    {t("cs.history_empty")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </section>
      </div>
    </RequireAuth>
  );
}

function Badge({ engine, model }: { engine: string; model?: string }) {
  return (
    <span
      className={`chip ${engine === "llm" ? "chip-mitre" : "chip-kz"}`}
      title={engine === "llm" ? "Локальная LLM (Ollama)" : "Детерминированный фолбэк"}
    >
      {engine === "llm" ? `LLM: ${model ?? "qwen3:8b"}` : "heuristic"}
    </span>
  );
}
